/**
 * Клиент API. Типы общие с сервером (@78/shared) — описывать их дважды
 * значит однажды разойтись и показать человеку не ту сумму.
 */

import type {
  ActorBody,
  AdminStatsResponse,
  CollectionsResponse,
  HoldingsResponse,
  MyCountersResponse,
  MyRequestsResponse,
  PositionsResponse,
  CountersResponse,
  CreateCounterBody,
  CreateRequestBody,
  DealDetailDto,
  OffersResponse,
  ProofPayloadResponse,
  PublicStatsResponse,
  RegistryResponse,
  ReferralCertBody,
  ReferralCertResponse,
  ReferralMeResponse,
  ReferralOwnerResponse,
  ReferralPubkeyResponse,
  SubscribeRequest,
  SubscribeResponse,
  RequestsResponse,
  WalletDealsResponse,
} from '@78/shared';

const BASE = import.meta.env['VITE_API_URL'] ?? '';

async function get<T>(path: string): Promise<T> {
  const res = await fetch(`${BASE}${path}`, { headers: { Accept: 'application/json' } });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? 'network');
  }
  return (await res.json()) as T;
}

export function fetchOffers(collection?: string): Promise<OffersResponse> {
  const q = collection === undefined ? '' : `?collection=${encodeURIComponent(collection)}`;
  return get<OffersResponse>(`/api/offers${q}`);
}

export function fetchDeal(address: string): Promise<DealDetailDto> {
  return get<DealDetailDto>(`/api/deals/${encodeURIComponent(address)}`);
}

export function fetchWalletDeals(address: string): Promise<WalletDealsResponse> {
  return get<WalletDealsResponse>(`/api/wallets/${encodeURIComponent(address)}/deals`);
}


export function fetchPositions(): Promise<PositionsResponse> {
  return get<PositionsResponse>('/api/positions');
}

/**
 * Реестр протокола. Без токена: список допущенных коллекций и валют нужен
 * обычному продавцу не меньше, чем админу.
 */
export function fetchRegistry(): Promise<RegistryResponse> {
  return get<RegistryResponse>('/api/registry');
}

/** Открытая сводка по сделкам — те же цифры, что и в блокчейне. */
export function fetchStats(): Promise<PublicStatsResponse> {
  return get<PublicStatsResponse>('/api/stats');
}

export function fetchAdminStats(): Promise<AdminStatsResponse> {
  return get<AdminStatsResponse>('/api/admin/stats');
}

export function fetchProofPayload(): Promise<ProofPayloadResponse> {
  return get<ProofPayloadResponse>('/api/proof-payload');
}

async function post<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const parsed = (await res.json()) as T | { error: string };
  if (!res.ok) {
    throw new Error('error' in (parsed as object) ? (parsed as { error: string }).error : 'network');
  }
  return parsed as T;
}

export function fetchRequests(collection?: string, viewer?: string): Promise<RequestsResponse> {
  const q = new URLSearchParams();
  if (collection !== undefined) q.set('collection', collection);
  // Кошелёк смотрящего — чтобы не показывать ему то, от чего он отказался.
  if (viewer !== undefined && viewer !== '') q.set('viewer', viewer);
  const tail = q.size === 0 ? '' : `?${q.toString()}`;
  return get<RequestsResponse>(`/api/requests${tail}`);
}

/** Отказ от чужого запроса: убирает его с доски отказавшегося. */
export function denyRequest(id: number, body: ActorBody): Promise<{ ok: boolean }> {
  return post(`/api/requests/${id}/deny`, body);
}

export function fetchCounters(requestId: number): Promise<CountersResponse> {
  return get<CountersResponse>(`/api/requests/${requestId}/counters`);
}

export function createRequest(
  body: CreateRequestBody,
): Promise<{ id: number; expiresAt: number; notified: number; ownerNotified: boolean }> {
  return post('/api/requests', body);
}

/**
 * Витрина: чьи NFT здесь можно попросить.
 *
 * Без коллекции — всё подряд: фишки сужают витрину, а не открывают её.
 */
export function fetchHoldings(collection: string | null): Promise<HoldingsResponse> {
  const q = collection === null ? '' : `?collection=${encodeURIComponent(collection)}`;
  return get<HoldingsResponse>(`/api/holdings${q}`);
}

export function createCounter(
  requestId: number,
  body: CreateCounterBody,
): Promise<{ id: number; expiresAt: number }> {
  return post(`/api/requests/${requestId}/counter`, body);
}

export function acceptCounter(counterId: number, body: ActorBody): Promise<{ ok: true }> {
  return post(`/api/counters/${counterId}/accept`, body);
}

export function declineCounter(counterId: number, body: ActorBody): Promise<{ ok: true }> {
  return post(`/api/counters/${counterId}/decline`, body);
}

export async function subscribe(body: SubscribeRequest): Promise<SubscribeResponse> {
  const res = await fetch(`${BASE}/api/subscribe`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const parsed = (await res.json()) as SubscribeResponse | { error: string };
  if (!res.ok) throw new Error('error' in parsed ? parsed.error : 'network');
  return parsed as SubscribeResponse;
}

export function fetchMyRequests(address: string): Promise<MyRequestsResponse> {
  return get<MyRequestsResponse>(`/api/wallets/${encodeURIComponent(address)}/requests`);
}

export function fetchMyCounters(address: string): Promise<MyCountersResponse> {
  return get<MyCountersResponse>(`/api/wallets/${encodeURIComponent(address)}/counters`);
}

export function fetchCollections(): Promise<CollectionsResponse> {
  return get<CollectionsResponse>('/api/collections');
}

export interface NotifyPrefs {
  pay: boolean;
  grace: boolean;
  requests: boolean;
  board: boolean;
  /** Видны ли мои NFT в витрине коллекции. Не про письма, но про то же:
   *  насколько человек здесь виден. */
  holdings: boolean;
}

export function fetchNotifyPrefs(address: string): Promise<{ prefs: NotifyPrefs | null }> {
  return get(`/api/wallets/${encodeURIComponent(address)}/notify-prefs`);
}

export function saveNotifyPrefs(body: ActorBody & Partial<NotifyPrefs>): Promise<NotifyPrefs> {
  return post('/api/notify-prefs', body);
}

export interface HistoryEvent {
  dealAddress: string;
  kind: string;
  amount: string;
  ts: number;
  role: 'buyer' | 'seller';
}

export function fetchHistory(address: string): Promise<{ events: HistoryEvent[] }> {
  return get(`/api/wallets/${encodeURIComponent(address)}/history`);
}

export function cancelRequest(id: number, body: ActorBody): Promise<{ ok: true }> {
  return post(`/api/requests/${id}/cancel`, body);
}

/**
 * Приглашения.
 *
 * Своя ссылка и итоги — только от имени подтверждённого кошелька: иначе
 * ссылку можно было бы выпустить на чужой адрес и собирать на него чужую
 * долю комиссии.
 */
export function fetchReferralMe(body: ActorBody): Promise<ReferralMeResponse> {
  return post('/api/referral/me', body);
}

/**
 * Приглашение для покупки. Берётся до платежа: адрес приглашающего и подпись
 * кладутся в forward_payload предоплаты, а проверяет подпись контракт.
 *
 * Отказ не мешает купить — просто без приглашения, поэтому и ответ не
 * ошибка, а `{ ok: false, reason }`.
 */
export function fetchReferralCert(body: ReferralCertBody): Promise<ReferralCertResponse> {
  return post('/api/referral/cert', body);
}

/** Ключ, которым сервер подписывает приглашения. Нужен панели для сверки. */
export function fetchReferralPubkey(): Promise<ReferralPubkeyResponse> {
  return get<ReferralPubkeyResponse>('/api/referral/pubkey');
}

/** Чей это код приглашения — чтобы ссылка вела к его офферам и запросам. */
export function fetchReferralOwner(code: string): Promise<ReferralOwnerResponse> {
  return get<ReferralOwnerResponse>(`/api/referral/of/${encodeURIComponent(code)}`);
}

/**
 * Отметка о переходе по ссылке. Кошелёк не нужен — на момент перехода его
 * ещё нет, а аккаунт Telegram уже известен.
 */
/**
 * Сообщает о сбое, который случился здесь, на экране.
 *
 * Нужен потому, что отправка транзакции идёт мимо нашего сервера — из
 * браузера в кошелёк через мост TON Connect. Всё, что там ломается, в
 * серверных логах не видно вовсе.
 */
export function reportClientError(body: {
  initData: string;
  where: string;
  message: string;
}): Promise<{ ok: true }> {
  return post('/api/client-error', body);
}

export function recordReferralVisit(body: {
  initData: string;
  code: string;
}): Promise<{ ok: boolean; reason?: string }> {
  return post('/api/referral/visit', body);
}

/**
 * Сделки, которые ждут взноса этого покупателя.
 *
 * Пока предоплата не внесена, покупателя в сделке нет — по адресу её не
 * найти. Связь идёт через принятый им запрос.
 */
export function fetchAwaitingDeals(address: string): Promise<WalletDealsResponse> {
  return get<WalletDealsResponse>(`/api/wallets/${encodeURIComponent(address)}/awaiting`);
}

/**
 * Текст, который получит продавец. Собирается на сервере тем же кодом, что
 * и настоящее письмо: превью, написанное отдельно, однажды разошлось бы с
 * ним и стало бы враньём.
 */
export function fetchRequestPreview(body: {
  totalPrice: string;
  upfront: string;
  period: number;
  nPayments: number;
  lang: string;
}): Promise<{ text: string }> {
  return post('/api/requests/preview', body);
}
